import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import { useOrderCreateCtx } from "../../../../../../context_providers/order_create_context";
import {
  ToastType,
  useToast,
} from "../../../../../../context_providers/toast/toast_ctx";
import { CustomerDetailModel } from "../../../../../../service/models/orders/customer_detail_model";
import { GetBillingProfilesOfUserAndTypeApi } from "../../../../../../service/repos/customer/billing_profile_repo";
import ModalDialog from "../../../../../new_components/common/modal";
import AddBillingProfileWidget from "./add_billing_profile_widget";

interface NewDeliveryProfileWidgetProps {}

const NewDeliveryProfileWidget: FunctionComponent<
  NewDeliveryProfileWidgetProps
> = () => {
  const { showToast } = useToast();
  const { user, deliveryProfile, setDeliveryProfile } = useOrderCreateCtx();
  const [same, setSame] = useState(true);
  const [addNew, setAddNew] = useState(false);
  const [edit, setEdit] = useState(false);
  const [customers, setCustomers] = useState<CustomerDetailModel[]>([]);
  const [loading, setLoading] = useState(false);

  const getCustomers = async () => {
    setLoading(true);
    const res = await GetBillingProfilesOfUserAndTypeApi(user?.uid, "Delivery");

    if (res.success) {
      setCustomers(res.data.billing_profiles);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  useEffect(() => {
    console.log("Customers", customers);
  }, [customers]);

  useEffect(() => {
    getCustomers();
  }, [user]);

  useEffect(() => {
    if (deliveryProfile) {
      setCustomers((o) => {
        const exists = o.some((c) => c.id === deliveryProfile.id);
        return exists ? o : [...o, deliveryProfile];
      });
    }
  }, [deliveryProfile]);

  const gstNumber = user?.gst_details?.gst_number ?? user?.gst ?? "--";
  const gstDetails = user?.gst_details?.gst_registration ?? "--";
  const customerGroup =
    user?.additional_customer_details?.customer_group ?? "--";
  const customerType = user?.additional_customer_details?.customer_type ?? "--";

  return (
    <>
      {addNew && (
        <ModalDialog
          id="order-create-delivery-add-modal"
          title="Add Delivery Address"
          show={addNew}
          onClose={() => setAddNew(false)}
        >
          <div className="p-5">
            <AddBillingProfileWidget
              id_prefix="order-create-delivery"
              user_id={user?.uid}
              onAdded={(data) => {
                setDeliveryProfile(data);
                setCustomers((o) => [...o, data]);
                setAddNew(false);
              }}
            />
          </div>
        </ModalDialog>
      )}

      <div className="flex justify-between gap-2 items-end my-2 mt-5 ">
        <div className=""></div>
        <div className="flex gap-2 md:mr-12">
          {edit && (
            <button
              id="order-create-delivery-cancel-btn"
              disabled={same}
              onClick={() => {
                setEdit(false);
              }}
              className="cursor-pointer border  px-3 py-1 rounded border-red-400   text-red-400 font-semibold"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="w-full min-w-0">
        {/* Mobile View */}
        <div className="md:hidden min-w-0">
          <div
            className="
              flex
              flex-nowrap
              gap-3
              w-full
              max-w-full
              overflow-x-auto
              snap-x
              snap-mandatory
              px-2
              pb-2
            "
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {customers.map((customer, i) => (
              <div
                key={i}
                id={`order-create-delivery-card-mobile-${i}`}
                style={{ flex: "0 0 85%" }}
                className={`
            snap-start
            bg-white
            rounded-xl
            border
            p-3
            shadow-sm
            cursor-pointer
            ${
              deliveryProfile?.id === customer.id
                ? "border-green-400 bg-green-50"
                : "border-gray-200"
            }
          `}
                onClick={() => {
                  if (deliveryProfile?.id === customer.id) {
                    setDeliveryProfile(undefined);
                  } else {
                    setDeliveryProfile(customer);
                  }
                  setEdit(false);
                }}
              >
                <div className="flex justify-between items-start mb-3">
                  <input
                    id={`order-create-delivery-radio-mobile-${i}`}
                    type="radio"
                    checked={deliveryProfile?.id === customer.id}
                    readOnly
                    className="w-5 h-5"
                    style={{ accentColor: "green" }}
                  />

                  <span className="rounded-full px-3 py-1 text-sm bg-green-50 text-teal-600">
                    {customer.address.type}
                  </span>
                </div>

                <div className="font-semibold text-base break-words">
                  {customer.firstName}
                  {customer.branch ? `: ${customer.branch}` : ""}
                </div>
                {(customer as any).spoc_name && (
                  <div className="text-sm text-teal-700 font-medium break-words">
                    Contact (SPOC): {(customer as any).spoc_name}
                    {(customer as any).spoc_mobile
                      ? ` · ${(customer as any).spoc_mobile}`
                      : ""}
                  </div>
                )}

                <div className="mt-2 text-sm break-words">
                  {[customer.mobile, customer.alt_mobile]
                    .filter(Boolean)
                    .join(", ")}
                </div>

                <div className="text-sm text-gray-500 break-words">
                  {customer.email}
                </div>

                <hr className="my-3" />

                <div className="text-sm">
                  <div>
                    <span className="font-semibold">Phone:</span>{" "}
                    {customer.mobile ?? "--"}
                  </div>
                  <div>
                    <span className="font-semibold">GST Number:</span>{" "}
                    {gstNumber}
                  </div>
                  <div>
                    <span className="font-semibold">GST Details:</span>{" "}
                    {gstDetails}
                  </div>
                  <div>
                    <span className="font-semibold">CA:</span>{" "}
                    {customer.ca ?? "--"}
                  </div>
                  <div>
                    <span className="font-semibold">Customer Group:</span>{" "}
                    {customerGroup}
                  </div>
                  <div>
                    <span className="font-semibold">Customer Type:</span>{" "}
                    {customerType}
                  </div>
                </div>

                <hr className="my-3" />

                <div className="text-sm text-gray-500 break-words">
                  {[
                    customer.address.line1,
                    customer.address.line2,
                    customer.address.city,
                    customer.address.state,
                    customer.address.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  {customer.address.pincode && ` - ${customer.address.pincode}`}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden md:block">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 w-full">
            {customers.map((customer, i) => (
                <div
                  key={i}
                  id={`order-create-delivery-card-${i}`}
                  className={`
              cursor-pointer
              bg-white
              hover:bg-green-50
              min-h-[240px]
              p-5
              rounded-lg
              border
              transition-all
              ${
                deliveryProfile?.id === customer.id
                  ? "border-green-400"
                  : "border-gray-200"
              }
            `}
                  onClick={() => {
                    if (deliveryProfile?.id === customer.id) {
                      setDeliveryProfile(undefined);
                    } else {
                      setDeliveryProfile(customer);
                    }
                    setEdit(false);
                  }}
                >
                  <div className="flex justify-between mb-3">
                    <input
                      id={`order-create-delivery-radio-${i}`}
                      type="radio"
                      checked={deliveryProfile?.id === customer.id}
                      readOnly
                      className="w-5 h-5"
                      style={{ accentColor: "green" }}
                    />

                    <span className="rounded-full px-3 py-1 bg-green-50 text-teal-600">
                      {customer.address.type}
                    </span>
                  </div>

                  <div className="font-semibold">
                    {customer.firstName}
                    {customer.branch ? `: ${customer.branch}` : ""}
                  </div>
                  {(customer as any).spoc_name && (
                    <div className="text-sm text-teal-700 font-medium">
                      Contact (SPOC): {(customer as any).spoc_name}
                      {(customer as any).spoc_mobile
                        ? ` · ${(customer as any).spoc_mobile}`
                        : ""}
                    </div>
                  )}

                  <div className="mt-2">
                    {[customer.mobile, customer.alt_mobile]
                      .filter(Boolean)
                      .join(", ")}
                  </div>

                  <div className="text-gray-500">{customer.email}</div>

                  <hr className="my-3" />

                  <div>
                    <span className="font-semibold">Phone:</span>{" "}
                    {customer.mobile ?? "--"}
                  </div>
                  <div>
                    <span className="font-semibold">GST Number:</span>{" "}
                    {gstNumber}
                  </div>
                  <div>
                    <span className="font-semibold">GST Details:</span>{" "}
                    {gstDetails}
                  </div>
                  <div>
                    <span className="font-semibold">CA:</span>{" "}
                    {customer.ca ?? "--"}
                  </div>
                  <div>
                    <span className="font-semibold">Customer Group:</span>{" "}
                    {customerGroup}
                  </div>
                  <div>
                    <span className="font-semibold">Customer Type:</span>{" "}
                    {customerType}
                  </div>

                  <hr className="my-3" />

                  <div className="text-sm text-gray-500">
                    {[
                      customer.address.line1,
                      customer.address.line2,
                      customer.address.city,
                      customer.address.state,
                      customer.address.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    {customer.address.pincode &&
                      ` - ${customer.address.pincode}`}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default NewDeliveryProfileWidget;
